import { Component, EventEmitter, Input, Output, OnInit, AfterViewInit, DestroyRef, OnDestroy } from '@angular/core';
import { FormBuilder, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { ContactForm } from '@root/app.interfaces';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatPostcodeSuggestionText } from '@shared/form-fields/places-autocomplete/helpers/postcode-suggestion.helper';
import { createPendingSubmitWaiter, hasEmptyRequiredField } from '@features/contact-form/form.helpers';

@Component({
	selector: 'app-contact-form',
	templateUrl: './contact-form.component.html',
	styleUrls: ['./contact-form.component.scss', './contact-from.responsive.scss'],
})
export class ContactFormComponent implements OnInit, AfterViewInit, OnDestroy {
	protected readonly formatPostcodeFn = formatPostcodeSuggestionText;
	protected readonly Validators = Validators;

	@Input() loading: boolean;

	@Output() submitEvent: EventEmitter<ContactForm> = new EventEmitter();

	private readonly savedRawValues = this.getFromFromStorage();
	form = this.fb.group({
		first_name: [this.savedRawValues?.first_name ?? '', [Validators.required, Validators.minLength(2)]],
		last_name: [this.savedRawValues?.last_name ?? '', [Validators.required, Validators.minLength(2)]],
		phone: [this.savedRawValues?.phone ?? '', [Validators.required, Validators.minLength(3)]],
		post_code: [this.savedRawValues?.post_code ?? '', [Validators.required]],
		email: [this.savedRawValues?.email ?? '', [Validators.required]],
		confirm_email: [this.savedRawValues?.confirm_email ?? '', [Validators.required, this.emailValidator('email')]],
		comments: [this.savedRawValues?.comments ?? null, [Validators.minLength(10)]],
		contact: [this.savedRawValues?.contact ?? ''],
	});

	submitted = false;
	embeddedRetailer = false;
	countryCode = this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code);

	private readonly waitForPendingAndSubmit = createPendingSubmitWaiter(this.form, () => this.onSubmit());

	constructor(
		private fb: FormBuilder,
		private sessionStorageService: SessionStorageService,
		private shareService: ShareService,
		private destroyRef: DestroyRef,
	) {}

	ngOnInit() {
		this.embeddedRetailer = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer);

		this.shareService.getCountry.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((code) => {
			this.countryCode = code;
		});
	}

	ngOnDestroy() {
		this.saveFormToStorage();
	}

	ngAfterViewInit() {
		this.updatePostcodeValidator();
	}

	protected get f() {
		return this.form.controls;
	}

	protected get someRequiredFieldsIsEmpty() {
		return hasEmptyRequiredField(this.f);
	}

	private emailValidator(confirmEmailInput: string): ValidatorFn {
		let confirmEmailControl: UntypedFormControl;
		let emailControl: UntypedFormControl;

		return (control: UntypedFormControl) => {
			if (!control.parent) {
				return null;
			}

			if (!confirmEmailControl) {
				confirmEmailControl = control;
				emailControl = control.parent.get(confirmEmailInput) as UntypedFormControl;
				emailControl.valueChanges.subscribe(() => {
					confirmEmailControl.updateValueAndValidity();
				});
			}

			if (emailControl.value.toLocaleLowerCase() !== confirmEmailControl.value.toLocaleLowerCase()) {
				return {
					notMatch: true,
				};
			}
			return null;
		};
	}

	updatePostcodeValidator(): void {
		const ctrl = this.form.get('post_code');

		if (!ctrl) {
			return;
		}

		if (this.embeddedRetailer) {
			ctrl?.clearValidators();
		} else {
			ctrl?.setValidators([Validators.required]);
		}

		ctrl?.updateValueAndValidity();
	}

	protected onSubmit(): void {
		this.submitted = true;

		if (this.form.pending) {
			this.waitForPendingAndSubmit();
			return;
		}

		if (this.form.invalid) {
			setTimeout(this.scrollToError.bind(this));
			return;
		}

		const fromRawValue = this.form.getRawValue();
		this.submitEvent.emit(fromRawValue as ContactForm);
	}

	private scrollToError() {
		const errorMessage = document.querySelectorAll(SELECTORS.contact_form_section_message)[0];
		const incorrectSection = errorMessage.closest(SELECTORS.contact_form_section);

		const offset = document.querySelector(SELECTORS.header)?.clientHeight + 10;
		const top = incorrectSection.getBoundingClientRect().top + window.pageYOffset - offset;

		if (window.pageYOffset > top) {
			window.scrollTo({ top, behavior: 'smooth' });
		}
	}

	private saveFormToStorage(): void {
		this.sessionStorageService.setSession(this.form.getRawValue(), STORAGE_NAMES.zip_contact_form);
	}

	private getFromFromStorage(): ContactForm | null {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_contact_form);
	}
}
